"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiPlus, FiTrash2 } from "react-icons/fi";
import { type CoverageDraft, CoverageFields, CoverageStatusToggles, coverageCreatePayload, coverageDraftError, coverageToDraft, coverageUpdatePayload, emptyCoverageDraft } from "@/components/insurance/coverage-form";
import { Badge, Button, Dialog, DialogBody, DialogContent, DialogTrigger } from "@/components/ui";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DialogClose, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/contexts/toaster";
import { parseError } from "@/lib/parse-error";
import { cx } from "@/lib/tailwind-utils";
import type { InsuranceCoverage } from "@/types/insurance-coverage";
import { useCreateInsuranceCoverageMutation, useDeleteInsuranceCoverageMutation, useUpdateInsuranceCoverageMutation } from "@/api/provider/user/patient/insurance-mutation";
import { insuranceQueryOptions } from "@/api/provider/user/patient/insurance-query";
interface InsuranceEditDialogProps {
  children?: React.ReactNode;
  patientId: string;
}
const InsuranceEditDialog: React.FC<InsuranceEditDialogProps> = t0 => {
  const $ = _c(20);
  const {
    children,
    patientId
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== patientId) {
    t1 = insuranceQueryOptions.list(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useQuery(t1);
  const coverages = t2 === undefined ? [] : t2;
  const contentRef = useRef(null);
  let t3;
  if ($[2] !== children) {
    t3 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      e.preventDefault();
      contentRef.current?.focus();
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = coverages.length === 0 ? "Add Insurance" : "Edit Insurance";
  let t6;
  if ($[5] !== t5) {
    t6 = <DialogHeader><DialogTitle>{t5}</DialogTitle></DialogHeader>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== open || $[8] !== patientId) {
    t7 = open && <InsuranceEditBody patientId={patientId} />;
    $[7] = open;
    $[8] = patientId;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <DialogBody>{t7}</DialogBody>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <DialogFooter><DialogClose asChild={true}><Button type="button" variant="secondary" onClick={() => setOpen(false)}>Close</Button></DialogClose></DialogFooter>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t6 || $[14] !== t8) {
    t10 = <DialogContent ref={contentRef} onOpenAutoFocus={t4}>{t6}{t8}{t9}</DialogContent>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== open || $[17] !== t10 || $[18] !== t3) {
    t11 = <Dialog open={open} onOpenChange={setOpen}>{t3}{t10}</Dialog>;
    $[16] = open;
    $[17] = t10;
    $[18] = t3;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  return t11;
};
interface InsuranceEditBodyProps {
  patientId: string;
}
function InsuranceEditBody(t0) {
  const $ = _c(21);
  const {
    patientId
  } = t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = insuranceQueryOptions.list(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2,
    isPending
  } = useQuery(t1);
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const coverages = t3;
  const [draftNew, setDraftNew] = useState(null);
  let t4;
  if ($[4] !== coverages) {
    t4 = [...coverages].sort(_temp);
    $[4] = coverages;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const sorted = t4;
  const hasNoCoverages = !isPending && sorted.length === 0;
  let t5;
  if ($[6] !== isPending) {
    t5 = isPending && <p className="text-body text-muted-foreground">Loading coverages…</p>;
    $[6] = isPending;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== patientId || $[9] !== sorted) {
    let t7;
    if ($[11] !== patientId) {
      t7 = coverage => <CoverageEditor key={coverage.id} coverage={coverage} patientId={patientId} />;
      $[11] = patientId;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = sorted.map(t7);
    $[8] = patientId;
    $[9] = sorted;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[13] !== draftNew || $[14] !== hasNoCoverages || $[15] !== patientId) {
    t7 = draftNew || hasNoCoverages ? <CoverageEditor isNew={true} patientId={patientId} draft={draftNew ?? {
      ...emptyCoverageDraft
    }} onCancel={draftNew ? () => setDraftNew(null) : undefined} onCreated={() => setDraftNew(null)} /> : <Button type="button" variant="secondary" size="sm" icon={FiPlus} onClick={() => setDraftNew({
      ...emptyCoverageDraft
    })}>Add insurance coverage</Button>;
    $[13] = draftNew;
    $[14] = hasNoCoverages;
    $[15] = patientId;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="space-y-3">{t5}{t6}{t7}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp(a, b) {
  return a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1;
}
interface CoverageEditorProps {
  patientId: string;
  coverage?: InsuranceCoverage;
  isNew?: boolean;
  draft?: CoverageDraft;
  onCancel?: () => void;
  onCreated?: () => void;
}
function CoverageEditor({
  patientId,
  coverage,
  isNew = false,
  draft: initialDraft,
  onCancel,
  onCreated
}: CoverageEditorProps) {
  const {
    toast
  } = useToast();
  const [draft, setDraft] = useState<CoverageDraft>(() => initialDraft ?? (coverage ? coverageToDraft(coverage) : {
    ...emptyCoverageDraft
  }));
  // DateOfBirthInput only writes back to its parent on a fully-typed valid
  // date; a partial edit leaves the draft pointing at the old value. Track
  // validity from the input so a Save during incomplete typing is blocked.
  const [policyHolderDobValid, setPolicyHolderDobValid] = useState(() => (initialDraft?.policyHolderDob ?? coverage?.policyHolderDob ?? "").length > 0);
  const [saving, setSaving] = useState(false);
  // Saved coverages open collapsed to a one-line summary; the draft opens expanded
  // because there is nothing to summarise yet. Keeps a patient with several
  // coverages from becoming one long undifferentiated scroll.
  const [isExpanded, setIsExpanded] = useState(isNew);
  // Track whether the user has explicitly toggled `isPrimary` locally — if
  // they haven't, sync from the server so a refetch (e.g. after another
  // coverage was promoted) demotes this draft, instead of re-promoting on
  // the next save.
  const [isPrimaryDirty, setIsPrimaryDirty] = useState(false);
  const externalIsPrimary = coverage?.isPrimary;
  useEffect(() => {
    if (externalIsPrimary === undefined || isPrimaryDirty) return;
    setDraft(d => d.isPrimary === externalIsPrimary ? d : {
      ...d,
      isPrimary: externalIsPrimary
    });
  }, [externalIsPrimary, isPrimaryDirty]);
  const createMutation = useCreateInsuranceCoverageMutation();
  const updateMutation = useUpdateInsuranceCoverageMutation();
  const deleteMutation = useDeleteInsuranceCoverageMutation();
  const valid = coverageDraftError(draft) === null && (policyHolderDobValid || !draft.policyHolderDob);
  async function handleSave() {
    if (!valid) return;
    setSaving(true);
    try {
      if (isNew) {
        await createMutation.mutateAsync({
          patientId,
          input: coverageCreatePayload(draft, patientId)
        });
        toast({
          title: "Insurance added",
          variant: "success"
        });
        onCreated?.();
      } else if (coverage) {
        await updateMutation.mutateAsync({
          patientId,
          coverageId: coverage.id,
          input: coverageUpdatePayload(draft)
        });
        // Clear the local-toggle flag so subsequent external refetches
        // (e.g. another row was just promoted) can sync `isPrimary` back in.
        setIsPrimaryDirty(false);
        toast({
          title: "Insurance updated",
          variant: "success"
        });
      }
    } catch (error) {
      const message = await parseError(error);
      toast({
        title: "Save failed",
        description: message,
        variant: "error"
      });
    } finally {
      setSaving(false);
    }
  }
  async function handleDelete() {
    if (!coverage) return;
    const label = coverage.insurerName || "this coverage";
    if (typeof window !== "undefined" && !window.confirm(`Remove ${label}? This can't be undone.`)) {
      return;
    }
    setSaving(true);
    try {
      await deleteMutation.mutateAsync({
        patientId,
        coverageId: coverage.id
      });
      toast({
        title: "Insurance removed",
        variant: "success"
      });
    } catch (error_0) {
      const message_0 = await parseError(error_0);
      toast({
        title: "Remove failed",
        description: message_0,
        variant: "error"
      });
    } finally {
      setSaving(false);
    }
  }
  const title = draft.insurerName || coverage?.insurerName || "Coverage";
  // One line that says what this coverage is, so a collapsed card is still useful.
  const summary = [draft.planName.trim(), draft.memberId.trim() && `Member ${draft.memberId.trim()}`, draft.claimsAddress.trim()].filter(Boolean).join(" · ");
  const body =
  // Adding a coverage is a plain form — no card, no collapse header. That
  // chrome exists to keep several *saved* coverages scannable; a single new
  // one has nothing to scan past.
  <div className={isNew ? "" : "border-t border-border px-3 py-3"}>
      <CoverageFields value={draft} onChange={setDraft} disabled={saving} onDobValidityChange={setPolicyHolderDobValid} />

      <div className="mt-4">
        <CoverageStatusToggles value={draft} onChange={setDraft} disabled={saving} onPrimaryToggled={() => setIsPrimaryDirty(true)} />
      </div>

      <div className={cx("mt-4 flex justify-end gap-2")}>
        {onCancel && <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Clear
          </Button>}
        <Button type="button" variant="primary" onClick={() => void handleSave()} disabled={!valid || saving} isLoading={saving}>
          {isNew ? "Add coverage" : "Save changes"}
        </Button>
      </div>
    </div>;
  if (isNew) return body;
  return <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="rounded-lg border border-border">
      {/* The Remove button is a sibling of the trigger, never a child — a button
          inside a button is invalid markup and trips hydration. */}
      <div className="flex items-center gap-2 px-3 py-2">
        <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <FiChevronDown className={cx("h-4 w-4 shrink-0 text-foreground-secondary transition-transform", !isExpanded && "-rotate-90")} aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-body-emphasis text-foreground">
                {title}
              </span>
              {draft.isPrimary && <Badge variant="neutral">Primary</Badge>}
              {!draft.inNetwork && <Badge variant="neutral">Out-of-network</Badge>}
            </div>
            {!isExpanded && summary && <p className="truncate text-caption text-muted-foreground">
                {summary}
              </p>}
          </div>
        </CollapsibleTrigger>
        {coverage && <Button type="button" variant="ghost" size="sm" icon={FiTrash2} onClick={() => void handleDelete()} disabled={saving}>
            Remove
          </Button>}
      </div>

      <CollapsibleContent>{body}</CollapsibleContent>
    </Collapsible>;
}
export default InsuranceEditDialog;